import React, { useMemo } from 'react';
import {
  useTable,
  useBlockLayout,
  useSortBy,
  Row,
  HeaderGroup
} from 'react-table';
import { useSticky } from 'react-table-sticky';
import { getLuminance } from '@material-ui/core';

import MDBox from '@components/MDBox';
import MDTypography from '@components/MDTypography';
import { dataTableTotalKeyword } from '@constants/common';

import './styles.scss';
import StickyHeader from './StickyHeader';
import { sortedValue } from './StickyHeader';

const StickyTable = ({
  columns,
  data,
  colorMap
}: {
  columns: any[];
  data: any;
  colorMap?: Record<number, string>;
}) => {
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 150,
      width: 100 / columns.length + '%',
      maxWidth: 700
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn
      },
      useBlockLayout,
      useSticky,
      useSortBy
    );

  const lastRow = useMemo(() => rows.pop() as Row<object>, [data]);

  prepareRow(lastRow);

  const sortedValue = (column: HeaderGroup<object>): sortedValue => {
    let sortedValue: sortedValue;

    if (column.isSorted) sortedValue = column.isSortedDesc ? 'desc' : 'asce';
    else sortedValue = 'none';

    return sortedValue;
  };

  const getCellFontColor = (backgroundColor: string) =>
    getLuminance(backgroundColor) > 0.5 ? '#000' : '#fff';

  return (
    <MDBox
      style={{ width: 'fit-content', overflow: 'auto' }}
      id='stickyTableWrapper'
    >
      <MDBox
        {...getTableProps()}
        className='table sticky'
        style={{ maxHeight: '60vh' }}
      >
        <MDBox className='header'>
          {headerGroups.map((headerGroup, key) => (
            <MDBox
              {...headerGroup.getHeaderGroupProps()}
              className='tr'
              key={key}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {headerGroup.headers.map((column, headerKey) => (
                <StickyHeader
                  key={String(column?.Header) || headerKey}
                  column={column}
                  sorted={sortedValue(column)}
                />
              ))}
            </MDBox>
          ))}
        </MDBox>

        <MDBox {...getTableBodyProps()} className='body'>
          {rows.map((row, index) => {
            prepareRow(row);

            return (
              <MDBox
                {...row.getRowProps()}
                className='tr'
                key={row?.id || index}
              >
                {row.cells.map((cell: any, cellIndex) => (
                  <MDBox
                    {...cell.getCellProps()}
                    className='td'
                    key={cell?.id || cellIndex}
                    bgcolor={
                      cellIndex > 0 &&
                      cell.column.Header !== dataTableTotalKeyword
                        ? `${
                            colorMap?.[cell.value] ??
                            colorMap?.[
                              cell?.row?.original[cell?.column?.Header]
                            ]
                          } !important`
                        : '#fff'
                    }
                    textAlign={cellIndex !== 0 ? 'right' : null}
                  >
                    <MDBox
                      component='div'
                      py={0}
                      px={1}
                      sx={({ palette: { info }, typography: { size } }) => ({
                        fontSize: size.sm,
                        color: info.main
                      })}
                    >
                      <MDBox
                        display='inline-block'
                        width='max-content'
                        color='dark'
                        sx={{
                          verticalAlign: 'middle',
                          color:
                            cellIndex > 0 &&
                            cell.column.Header !== dataTableTotalKeyword
                              ? `${getCellFontColor(
                                  colorMap?.[cell.value] ??
                                    colorMap?.[
                                      cell?.row?.original[cell?.column?.Header]
                                    ] ??
                                    '#fff'
                                )} !important`
                              : ''
                        }}
                      >
                        {cell.render('Cell')}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            );
          })}
        </MDBox>

        <MDBox className='footer'>
          <MDBox {...rows[0]?.getRowProps()} className='tr' key='da'>
            {(lastRow as any)?.cells.map((cell: any, index: number) => (
              <MDBox
                {...cell?.getCellProps()}
                className='td'
                key={cell?.id || index}
                textAlign={index !== 0 ? 'right' : null}
              >
                <MDBox component='div' py={0} px={1}>
                  <MDBox position='relative' color='secondary' opacity={0.7}>
                    <MDTypography
                      variant='h6'
                      color='info'
                      sx={() => ({
                        textTransform: 'uppercase',
                        color: `info`
                      })}
                    >
                      {cell.render('Cell')}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

export default StickyTable;
